import type { ReactNode } from 'react';
import { EmptyState, StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AdminHealth } from './health-types';
import { healthSummary } from './health-words';
import { AlertsPanel } from './AlertsPanel';
import { TicketsPanel } from './TicketsPanel';
import { GpuPoolsPanel } from './GpuPoolsPanel';
import '../admin.css';
import '../pools/pools.css';
import './health.css';

export interface HealthBoardProps {
  view: AdminHealth;
  /** Set when the last refresh failed and this is the picture from before it: what failed, in a sentence. */
  refreshFailed?: string | undefined;
  onRetry?: (() => void) | undefined;
}

/** The answer in one line, and the watcher's own pulse: its last loop, since when it runs, mails and tickets. */
function Summary({ view, now }: { view: AdminHealth; now: number }): ReactNode {
  const f = useFormat();
  const s = healthSummary(view);
  const w = view.watcher;
  return (
    <section className={`pl-summary pl-summary--${s.tone}`} aria-label="Summary">
      <p className="pl-summary__head">
        <StatusDot tone={s.tone} label={s.headline} />
        <strong>{s.headline}</strong>
        {view.alerts.open.length > 0 && !w.stale ? (
          <span>{f.int(view.alerts.open.length)} open</span>
        ) : null}
      </p>
      <dl className="pl-summary__facts">
        <div>
          <dt>Last check</dt>
          <dd className={w.stale ? 'hl-late' : undefined}>
            {f.ago(w.lastLoopAt, now)}
            {w.loopMs !== null && !w.stale ? ` · ${f.decimal(w.loopMs / 1000, 1)} s` : ''}
          </dd>
        </div>
        <div>
          <dt>Running since</dt>
          <dd>{w.startedAt ? f.ago(w.startedAt, now) : 'not known'}</dd>
        </div>
        <div>
          <dt>Alert mails, 24 h</dt>
          <dd>{f.int(view.alerts.mailsLastDay)}</dd>
        </div>
        <div>
          <dt>Cloud card-hours today</dt>
          <dd>{f.decimal(view.pools.todayHours, 1)} h</dd>
        </div>
      </dl>
    </section>
  );
}

function Notices({ view, refreshFailed, onRetry }: HealthBoardProps): ReactNode {
  const f = useFormat();
  return (
    <>
      {refreshFailed ? (
        <p className="pl-notice" role="status">
          <span>Could not refresh: {refreshFailed} Showing the last picture.</span>
          {onRetry ? (
            <button type="button" className="pl-notice__retry" onClick={onRetry}>
              Retry
            </button>
          ) : null}
        </p>
      ) : null}
      {view.watcher.stale && view.watcher.lastLoopAt !== null ? (
        <p className="pl-notice hl-notice--down" role="alert">
          The health monitor last checked {f.ago(view.watcher.lastLoopAt, Date.parse(view.asOf))}.
          Until it runs again nothing is checked, mailed or ticketed, and pools are not started or
          stopped.
        </p>
      ) : null}
    </>
  );
}

function Group({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="pl-group" aria-label={title}>
      <h2 className="pl-group__title">
        {title} <span>{note}</span>
      </h2>
      {children}
    </section>
  );
}

/**
 * Operations, Health: what the health monitor knows, from its own records. Whether it is running, the alerts
 * open and lately closed, the Desk tickets it opened, and the cloud GPU pools it starts and stops with their
 * card-hours. Before its first record it says so instead of showing empty lists.
 */
export function HealthBoard(props: HealthBoardProps): ReactNode {
  const { view } = props;
  const now = Date.parse(view.asOf);
  if (view.watcher.lastLoopAt === null && view.alerts.open.length === 0) {
    return (
      <EmptyState
        title="The health monitor has not reported yet"
        description="This fills in within a minute of the health monitor starting. If it stays empty, the monitor is not running or cannot write its record."
      />
    );
  }
  return (
    <div className="pl-board">
      <Notices {...props} />
      <Summary view={view} now={now} />
      <div className="hl-pair">
        <Group title="Alerts" note="open now, and closed lately">
          <AlertsPanel alerts={view.alerts} now={now} />
        </Group>
        <Group title="Desk tickets" note="opened by the health monitor, last 14 days">
          <TicketsPanel tickets={view.tickets} now={now} />
        </Group>
      </div>
      <Group title="Cloud GPU pools" note="started when work waits, stopped when idle">
        <GpuPoolsPanel pools={view.pools} now={now} />
      </Group>
    </div>
  );
}
